






























































































































import Vue, { PropType } from 'vue';
import {
  ComputationNodeSubtype,
  IServiceComputationNode,
  IQuoteServiceTemplate,
  ServiceComputationNode,
  ServiceComputationNodeFieldError
} from '@/models';

import { inject as RegistrableInject } from '@/mixins/registrable';
import BindsAttrs from '@/mixins/binds-attrs';
import mixins from '@/utils/mixins';

import CSIItemSearch from '../csi/ItemSearch.vue';
import QuoteServiceVariableSearch from '../quoteservicevariable/QuoteServiceVariableSearch.vue';
import QuoteServiceComputationSearch from '../quote-service-computation/QuoteServiceComputationSearch.vue';
import ItemTagPicker from '../item/ItemTagPicker.vue';

const baseMixins = mixins(
  BindsAttrs,
  RegistrableInject<'formExpression', any>('formExpression'),
  RegistrableInject<'template', any>('template')
);

type ErrorBag = Record<number, boolean>;
interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: Vue & {
      validate: () => boolean;
      resetValidation: () => void;
      reset: () => void;
    };
  };
  template: IQuoteServiceTemplate;
}
export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    components: {
      CSIItemSearch,
      QuoteServiceVariableSearch,
      QuoteServiceComputationSearch,
      ItemTagPicker
    },
    name: 'ComputationOperandNode',

    props: {
      value: {
        type: Object as PropType<ServiceComputationNode>
      },

      fieldError: {
        type: Object as PropType<ServiceComputationNodeFieldError>,
        default: () => new ServiceComputationNodeFieldError()
      },

      disabled: {
        type: Boolean,
        default: false
      },

      firstNode: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        valid: false,
        nodeOperandSubtypeList: [
          { text: 'Constant', value: ComputationNodeSubtype.constant },
          { text: 'Variable', value: ComputationNodeSubtype.variable },
          {
            text: 'Item Quantity',
            value: ComputationNodeSubtype.item_quantity
          },
          {
            text: 'Days Computation',
            value: ComputationNodeSubtype.days_computation
          },
          { text: 'Item Exists', value: ComputationNodeSubtype.item_exists },
          {
            text: 'Item Tag Quantity',
            value: ComputationNodeSubtype.item_tag_quantity
          },
          {
            text: 'Item Tag Exists',
            value: ComputationNodeSubtype.item_tag_exists
          },
          {
            text: 'Item Tag Machine Quantity',
            value: ComputationNodeSubtype.item_tag_machine_quantity
          }
        ],
        serviceTemplateId: null as number | null,
        errorBag: {} as ErrorBag
      };
    },

    computed: {
      hasError(): boolean {
        return !this.valid;
      },

      internalValue: {
        get(): IServiceComputationNode {
          return this.value;
        },
        set(val: IServiceComputationNode) {
          // hack for passing multiple function on the emit values
          this.$emit('input', val);
        }
      },

      isSubtypeConstant(): boolean {
        return this.internalValue.subtype === ComputationNodeSubtype.constant;
      },

      isSubtypeVariable(): boolean {
        return this.internalValue.subtype === ComputationNodeSubtype.variable;
      },

      isSubtypeItemQuantity(): boolean {
        return (
          this.internalValue.subtype === ComputationNodeSubtype.item_quantity
        );
      },

      isSubtypeItemExists(): boolean {
        return (
          this.internalValue.subtype === ComputationNodeSubtype.item_exists
        );
      },

      isSubtypeDaysComputation(): boolean {
        return (
          this.internalValue.subtype === ComputationNodeSubtype.days_computation
        );
      },

      isSubtypeItemTagQuantity(): boolean {
        return (
          this.internalValue.subtype ===
          ComputationNodeSubtype.item_tag_quantity
        );
      },

      isSubtypeItemTagExists(): boolean {
        return (
          this.internalValue.subtype === ComputationNodeSubtype.item_tag_exists
        );
      },

      isSubtypeItemTagMachineQuantity(): boolean {
        return (
          this.internalValue.subtype ===
          ComputationNodeSubtype.item_tag_machine_quantity
        );
      },

      // rules
      operandRules() {
        return [(v: string) => v != null || 'An operand type is required'];
      },
      constantRules() {
        return [
          (v: number) => {
            if (!!v && !isNaN(v)) return true;
            return 'A number value is required';
          }
        ];
      },
      variableRules() {
        return [(v: string) => v != null || 'A variable is required'];
      },
      itemRules() {
        return [(v: string) => (!!v && v.length > 0) || 'An item is required'];
      },
      daysComputationRules() {
        return [(v: string) => !!v || 'A computation is required'];
      },
      tagRules() {
        return [(v: string) => (!!v && v.length > 0) || 'A tag is required'];
      }
    },

    methods: {
      updateInteralValue(node: IServiceComputationNode) {
        this.internalValue = node;
      },

      setConstant(constant?: number) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          subtype: this.internalValue.subtype,
          order: this.internalValue.order,
          constant: constant
        });
        this.updateInteralValue(newNode);
      },

      setVariable(variable?: number) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          subtype: this.internalValue.subtype,
          order: this.internalValue.order,
          variable: variable
        });
        this.updateInteralValue(newNode);
      },

      setItem(items?: string[]) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          subtype: this.internalValue.subtype,
          order: this.internalValue.order,
          items: items
        });
        this.updateInteralValue(newNode);
      },

      setItemTags(tags?: number[]) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          subtype: this.internalValue.subtype,
          order: this.internalValue.order,
          item_tags: tags
        });
        this.updateInteralValue(newNode);
      },

      setDaysComputation(computation: number) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          subtype: this.internalValue.subtype,
          order: this.internalValue.order,
          days_computation: computation
        });
        this.updateInteralValue(newNode);
      },

      setSubtype(subtype: number) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          order: this.internalValue.order,
          subtype: subtype
        });
        this.updateInteralValue(newNode);
      },

      /** @public */
      reset() {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          order: this.internalValue.order,
          subtype: ComputationNodeSubtype.constant
        });
        this.updateInteralValue(newNode);
        return this.$refs['form'].reset();
      },

      /** @public */
      resetValidation() {
        return this.$refs['form'].resetValidation();
      },

      validate(): boolean {
        return this.$refs['form'].validate();
      }
    },

    created() {
      this.formExpression && this.formExpression.register(this);
      this.template && this.template.register(this);
    },

    beforeDestroy() {
      this.formExpression && this.formExpression.unregister(this);
      this.template && this.template.unregister(this);
    }
  });
